
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { toast } from "react-toastify";
import "../styles/AttendeeManagementPage.css";
import DashboardLayout from "../components/DashboardLayout";

const AttendeeManagementPage = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();

    const [attendees, setAttendees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortColumn, setSortColumn] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("DESC");
    const [event, setEvent] = useState(null);
    const [attendeesData, setAttendeesData] = useState([]);


    
    const [detailsPanelWidth, setDetailsPanelWidth] = useState(460);
    const [isResizingDetails, setIsResizingDetails] = useState(false);

    const attendeeLayoutRef = useRef(null);


    const attendeeStats = React.useMemo(() => {
        const attendees = Array.isArray(attendeesData)
            ? attendeesData
            : [];

        return {
            total: attendees.length,
            approved: attendees.filter(
                (attendee) =>
                    String(attendee.status || "").toLowerCase() === "approved"
            ).length,
            pending: attendees.filter(
                (attendee) =>
                    String(attendee.status || "").toLowerCase() === "pending"
            ).length,
            rejected: attendees.filter(
                (attendee) =>
                    String(attendee.status || "").toLowerCase() === "rejected"
            ).length,
        };
    }, [attendeesData]);
    const token = localStorage.getItem("token");
    const perPage = 10;

    // Debounce attendee search
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchInput]);

    // Fetch attendee registrations
    const loadAttendees = useCallback(async () => {
        if (!token || !eventId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const params = new URLSearchParams({
                page: String(page),
                numPerPage: String(perPage),
                sortByColumnName: sortColumn,
                sortBy: sortOrder,
                searchBy: search,
            });

            const response = await fetch(
                `${API_BASE_URL}/attendee_registration/${eventId}?${params}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to load attendees (${response.status})`);
            }

            const data = await response.json();

            console.log("Attendee API response:", data);


            const eventData = data.result ?? data;

            setEvent(eventData);

            const attendeeList = Array.isArray(eventData.attendees)
                ? eventData.attendees
                : [];

            setAttendees(attendeeList);
            setAttendeesData(attendeeList);


            // Use backend pagination metadata if it is available.
            const pages = data?.totalPages ?? data?.total_pages;

            if (pages !== undefined && Number.isFinite(Number(pages))) {
                setTotalPages(Math.max(1, Number(pages)));
            } else {
                setTotalPages(attendeeList.length < perPage ? page : page + 1);
            }
        } catch (error) {
            console.error("Error loading attendees:", error);
            toast.error("Failed to load attendee registrations.");
            setAttendees([]);
        } finally {
            setLoading(false);
        }
    }, [eventId, token, page, sortColumn, sortOrder, search]);

    useEffect(() => {
        loadAttendees();
    }, [loadAttendees]);

    // Sort by clicking a table heading
    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder((current) => current === "ASC" ? "DESC" : "ASC");
        } else {
            setSortColumn(column);
            setSortOrder("ASC");
        }
        setPage(1);
    };

    // Approve or reject an attendee
    const handleAttendeeAction = async (registrationId, action) => {
        if (!token || !eventId || !registrationId) {
            toast.error("Missing attendee registration information.");
            return;
        }

        setActionLoading(registrationId);

        try {
            const response = await fetch(
                `${API_BASE_URL}/approve_attendee_registration`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        eventId,
                        registrationId,
                        action,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || "Action failed.");
            }

            const newStatus = action === "approve" ? "Approved" : "Rejected";

            setAttendees((previous) =>
                previous.map((attendee) =>
                    attendee.registration_id === registrationId
                        ? { ...attendee, status: newStatus }
                        : attendee
                )
            );

            toast.success(`Attendee ${newStatus.toLowerCase()} successfully.`);
        } catch (error) {
            console.error("Attendee action error:", error);
            toast.error(error.message || "Action failed. Please try again.");
        } finally {
            setActionLoading(null);
        }
    };

    // Download a CSV report
    const downloadCSV = async (endpoint, filename) => {
        if (!token || !eventId) {
            toast.error("Please sign in again.");
            return;
        }

        try {
            const response = await fetch(
                `${API_BASE_URL}/${endpoint}/${eventId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to download CSV.");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
            toast.success("Download started.");
        } catch (error) {
            console.error("Attendee CSV download error:", error);
            toast.error(error.message || "Failed to download CSV.");
        }
    };

    const getStatusClass = (status) => {
        switch ((status || "Pending").toLowerCase()) {
            case "approved":
                return "attendee-status-approved";
            case "rejected":
                return "attendee-status-rejected";
            default:
                return "attendee-status-pending";
        }
    };

    const changePage = (nextPage) => {
        if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
        setPage(nextPage);
    };

    
const [selectedAttendee, setSelectedAttendee] = useState(null);
const [attendeeDetails, setAttendeeDetails] = useState(null);
const [attendeeDetailsLoading, setAttendeeDetailsLoading] =
    useState(false);
const [attendeeDetailsError, setAttendeeDetailsError] = useState("");

const openAttendeeDetails = async (attendee) => {
    // Always use the numeric database ID.
    if (!attendee?.id) {
        setAttendeeDetailsError("Attendee database ID is missing.");
        return;
    }

    setSelectedAttendee(attendee);
    setAttendeeDetails(null);
    setAttendeeDetailsError("");
    setAttendeeDetailsLoading(true);

    try {
        const token = localStorage.getItem("token");

        const response = await fetch(
            `${API_BASE_URL}/get_attendee_registration_details/${attendee.id}`,
            {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
        );

        const result = await response.json();

        if (!response.ok) {
            throw new Error(
                result.message || `Failed to load details (${response.status})`
            );
        }

        setAttendeeDetails(result.data ?? result.result ?? result);
    } catch (error) {
        setAttendeeDetailsError(error.message);
    } finally {
        setAttendeeDetailsLoading(false);
    }
};

const closeAttendeeDetails = () => {
    setSelectedAttendee(null);
    setAttendeeDetails(null);
    setAttendeeDetailsError("");
};


useEffect(() => {
    if (!isResizingDetails) return;

    const handlePointerMove = (event) => {
        const layout = attendeeLayoutRef.current;
        if (!layout) return;

        const bounds = layout.getBoundingClientRect();
        const nextWidth = bounds.right - event.clientX;

        setDetailsPanelWidth(
            Math.max(320, Math.min(760, nextWidth))
        );
    };

    const stopResizing = () => {
        setIsResizingDetails(false);
    };

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", stopResizing);

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", stopResizing);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    };
}, [isResizingDetails]);


    return (
        <DashboardLayout currentSection="speaking">
            <main className="attendee-page">
                <div className="attendee-page-heading">
                    <div className="attendee-title-group">
                        <div className="attendee-page-icon">
                            <i className="fas fa-users" />
                        </div>

                        <div>
                            <h1>Attendee Management</h1>
                            <p>View and manage registrations for your event.</p>
                        </div>
                    </div>


                    <button
                        type="button"
                        className="attendee-btn attendee-btn-outline"
                        onClick={() => navigate("/dashboard")}
                    >
                        <i className="fas fa-arrow-left" /> Back to Events
                    </button>


                </div>


                {/* Attendee Statistics */}
                <div className="attendee-stats-grid">
                    <div className="attendee-stat-card">
                        <div className="attendee-stat-icon attendee-stat-total">
                            <i className="fas fa-users" />
                        </div>
                        <div className="attendee-stat-content">
                            <h3>{attendeeStats.total}</h3>
                            <p>Total Attendees</p>
                           
                        </div>
                    </div>

                    <div className="attendee-stat-card">
                        <div className="attendee-stat-icon attendee-stat-approved">
                            <i className="fas fa-check-circle" />
                        </div>
                        <div className="attendee-stat-content">
                            <h3>{attendeeStats.approved}</h3>
                            <p>Approved</p>
                            
                        </div>
                    </div>

                    <div className="attendee-stat-card">
                        <div className="attendee-stat-icon attendee-stat-pending">
                            <i className="fas fa-clock" />
                        </div>
                        <div className="attendee-stat-content">
                            <h3>{attendeeStats.pending}</h3>
                            <p>Pending Review</p>
                            
                        </div>
                    </div>

                    <div className="attendee-stat-card">
                        <div className="attendee-stat-icon attendee-stat-rejected">
                            <i className="fas fa-times-circle" />
                        </div>
                        <div className="attendee-stat-content">
                            <h3>{attendeeStats.rejected}</h3>
                            <p>Rejected</p>
                            
                        </div>
                    </div>
                </div>
<div
    ref={attendeeLayoutRef}
    className={`attendee-content-layout ${
        selectedAttendee ? "has-attendee-details" : ""
    }`}
    style={
        selectedAttendee
            ? {
                  "--attendee-details-width":
                      `${detailsPanelWidth}px`,
              }
            : undefined
    }
>

                <section className="attendee-management-card">



                    {/* Event Header + Toolbar */}
                    <div className="attendee-header-toolbar">

                        {/* Left: Event Logo and Details */}
                        <div className="attendee-card-header">
                            <div className="attendee-event-heading">
                                <img
                                    src={
                                        event?.banner_image?.base64 ||
                                        event?.banner_image?.url ||
                                        "/default-event-logo.png"
                                    }
                                    alt="Event logo"
                                    className="attendee-event-logo"
                                />

                                <div className="attendee-event-info">
                                    <h2>
                                        {event?.event_name || "Event Registrations"}
                                    </h2>
                                </div>
                            </div>
                        </div>

                        {/* Right: Buttons and Search */}
                        <div className="attendee-toolbar">

                            <div className="attendee-toolbar-buttons">
                                <button
                                    type="button"
                                    className="attendee-btn attendee-btn-primary"
                                    onClick={() =>
                                        navigate(`/free-event-register/event/${eventId}`)
                                    }
                                >
                                    <i className="fas fa-user-plus" />
                                    Add Attendee
                                </button>

                                <button
                                    type="button"
                                    className="attendee-btn attendee-btn-outline"
                                    onClick={() =>
                                        downloadCSV(
                                            "download_attendee_registration_customform",
                                            `event-${eventId}-attendee-form.csv`
                                        )
                                    }
                                >
                                    <i className="fas fa-download" />
                                    Download Form CSV
                                </button>
                            </div>

                            {/* Search Attendee */}
                            <div className="attendee-search">
                                <i className="fas fa-search" />

                                <input
                                    type="search"
                                    value={searchInput}
                                    onChange={(event) =>
                                        setSearchInput(event.target.value)
                                    }
                                    placeholder="Search attendee..."
                                    aria-label="Search attendees"
                                />

                                {searchInput && (
                                    <button
                                        type="button"
                                        className="attendee-clear-search"
                                        onClick={() => setSearchInput("")}
                                        aria-label="Clear search"
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>


                    <div className="attendee-table-wrapper">
                        <table className="attendee-table">
                            <thead>
                                <tr>
                                    <th>
                                        <i className="fas fa-user" style={{ marginRight: "6px" }}></i> Name
                                    </th>

                                    <th
                                        className="attendee-sortable"
                                        onClick={() => handleSort("email_id")}
                                    >
                                        <i className="fas fa-envelope" style={{ marginRight: "6px" }}></i> Email
                                        <span className="attendee-sort-indicator">
                                            {sortColumn === "email_id"
                                                ? sortOrder === "ASC" ? "▲" : "▼"
                                                : "↕"}
                                        </span>
                                    </th>

                                    <th
                                        className="attendee-sortable"
                                        onClick={() => handleSort("created_at")}
                                    >
                                        Submitted On
                                        <span className="attendee-sort-indicator">
                                            {sortColumn === "created_at"
                                                ? sortOrder === "ASC" ? "▲" : "▼"
                                                : "↕"}
                                        </span>
                                    </th>

                                    <th className="attendee-form-info-heading">
                                         <i className="fas fa-file-alt" style={{ marginRight: "6px" }} />
                                        Form Info
                                    </th>

                                   
<th
    className="attendee-sortable"
    onClick={() => handleSort("status")}
>
    <i
        className="fas fa-check-circle"
        style={{ marginRight: "6px" }}
    />
    Status
    <span className="attendee-sort-indicator">
        {sortColumn === "status"
            ? sortOrder === "ASC" ? "▲" : "▼"
            : "↕"}
    </span>
</th>


                                    <th>
                                        <i className="fas fa-sliders-h" style={{ marginRight: "6px" }}></i>
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="attendee-table-message">
                                            <i className="fas fa-spinner fa-spin" />
                                            Loading registrations...
                                        </td>
                                    </tr>
                                ) : attendees.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="attendee-table-message">
                                            No registrations found.
                                        </td>
                                    </tr>
                                ) : (
                                    attendees.map((attendee, index) => {
                                        const name = attendee.name || attendee.email_id || "—";
                                        const email = attendee.email_id || "—";
                                        const submittedOn = attendee.submitted_at || "—";
                                        const status = attendee.status || "Pending";
                                        const settled = ["approved", "rejected"].includes(
                                            status.toLowerCase()
                                        );
                                        const busy =
                                            actionLoading === attendee.registration_id;

                                        return (
                                            <tr key={attendee.registration_id || attendee.id || index}>
                                                <td className="attendee-name-cell">{name}</td>
                                                <td>{email}</td>
                                                <td>{submittedOn}</td>

                                                <td>
                                                    
<button
    type="button"
    className="attendee-btn attendee-btn-outline"
    onClick={() => openAttendeeDetails(attendee)}
>
    <i className="fas fa-file-alt" />
    View Form
</button>

                                                </td>

                                                <td>
                                                    <span className={`attendee-status ${getStatusClass(status)}`}>
                                                        {status}
                                                    </span>
                                                </td>

                                                <td>

                                                    <div className="attendee-manage-actions">
                                                        <button
                                                            type="button"
                                                            className="attendee-icon-btn attendee-icon-approve"
                                                            title="Approve attendee"
                                                            aria-label="Approve attendee"
                                                            disabled={settled || busy}
                                                            onClick={() =>
                                                                handleAttendeeAction(
                                                                    attendee.registration_id,
                                                                    "approve"
                                                                )
                                                            }
                                                        >
                                                            {busy ? (
                                                                <i className="fas fa-spinner fa-spin" />
                                                            ) : (
                                                                <i className="fas fa-check" />
                                                            )}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="attendee-icon-btn attendee-icon-reject"
                                                            title="Reject attendee"
                                                            aria-label="Reject attendee"
                                                            disabled={settled || busy}
                                                            onClick={() =>
                                                                handleAttendeeAction(
                                                                    attendee.registration_id,
                                                                    "reject"
                                                                )
                                                            }
                                                        >
                                                            <i className="fas fa-times" />
                                                        </button>
                                                    </div>


                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="attendee-pagination">
                        <span className="attendee-pagination-info">
                            Showing {attendees.length} registrations on this page
                        </span>

                        <div className="attendee-pagination-controls">
                            <button
                                type="button"
                                className="attendee-btn attendee-btn-outline"
                                disabled={loading || page <= 1}
                                onClick={() => changePage(page - 1)}
                            >
                                <i className="fas fa-chevron-left" /> Previous
                            </button>

                            <span className="attendee-page-count">
                                Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                            </span>

                            <button
                                type="button"
                                className="attendee-btn attendee-btn-outline"
                                disabled={loading || page >= totalPages}
                                onClick={() => changePage(page + 1)}
                            >
                                Next <i className="fas fa-chevron-right" />
                            </button>
                        </div>
                    </div>
                </section>

{selectedAttendee && (
    <aside className="attendee-details-panel">
        <div
            className="attendee-details-resizer"
            onPointerDown={(event) => {
                if (event.button !== undefined && event.button !== 0) {
                    return;
                }

                event.preventDefault();
                setIsResizingDetails(true);
            }}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize attendee details panel"
            title="Drag to resize"
        >
            <span />
        </div>

        <div className="attendee-details-header">
            <div>
                <h2>Registration Details</h2>
                <p>
                    {selectedAttendee.name || "Attendee"}
                </p>
            </div>

            <button
                type="button"
                className="attendee-details-close"
                onClick={closeAttendeeDetails}
                aria-label="Close details"
            >
                &times;
            </button>
        </div>

        <div className="attendee-details-body">
            {attendeeDetailsLoading ? (
                <p>Loading registration details...</p>
            ) : attendeeDetailsError ? (
                <p className="attendee-details-error">
                    {attendeeDetailsError}
                </p>
            ) : (
                <div>
                    {Object.entries(
                        attendeeDetails?.data ??
                        attendeeDetails?.result ??
                        attendeeDetails ??
                        {}
                    )
                        .filter(
                            ([key]) =>
                                !["success", "message"].includes(
                                    key.toLowerCase()
                                )
                        )
                        .map(([key, value]) => (
                            <div
                                className="attendee-details-field"
                                key={key}
                            >
                                <span>
                                    {key
                                        .replace(/_/g, " ")
                                        .replace(/\b\w/g, (char) =>
                                            char.toUpperCase()
                                        )}
                                </span>

                                <p>
                                    {value == null || value === ""
                                        ? "Not provided"
                                        : typeof value === "object"
                                          ? JSON.stringify(value, null, 2)
                                          : String(value)}
                                </p>
                            </div>
                        ))}
                </div>
            )}
        </div>
    </aside>
)}

                </div>
            </main>
        </DashboardLayout>
    );
};

export default AttendeeManagementPage;
